import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/** Services tiers soumis au consentement (les seuls traceurs non strictement nécessaires du site). */
export type ConsentTarget = 'maps' | 'youtube';

export const CONSENT_SERVICES: readonly ConsentTarget[] = ['maps', 'youtube'];

export type ConsentChoices = Record<ConsentTarget, boolean>;

interface StoredConsent {
  version: number;
  savedAt: number;
  choices: ConsentChoices;
}

const STORAGE_KEY = 'bdp_consent';
const STORAGE_VERSION = 1;
/** La CNIL recommande de redemander le choix au plus tard tous les 6 mois. */
const VALIDITY_MS = 182 * 24 * 60 * 60 * 1000;

const NO_CONSENT: ConsentChoices = { maps: false, youtube: false };

@Injectable({ providedIn: 'root' })
export class ConsentManager {
  private platformId = inject(PLATFORM_ID);

  private _ready = signal(false);
  private _choices = signal<ConsentChoices>({ ...NO_CONSENT });
  private _decided = signal(false);
  private _preferencesOpen = signal(false);
  private _draft = signal<ConsentChoices>({ ...NO_CONSENT });

  /** Vrai une fois le stockage lu côté navigateur (jamais vrai pendant le rendu serveur). */
  readonly ready = this._ready.asReadonly();
  readonly choices = this._choices.asReadonly();
  /** Vrai si le visiteur a déjà fait un choix valide (moins de 6 mois). */
  readonly decided = this._decided.asReadonly();
  readonly preferencesOpen = this._preferencesOpen.asReadonly();
  /** Choix en cours d'édition dans la fenêtre de préférences (non enregistrés). */
  readonly draft = this._draft.asReadonly();
  readonly bannerVisible = computed(() => this._ready() && !this._decided() && !this._preferencesOpen());

  constructor() {
    // La lecture se fait après le premier rendu (tâche différée) : le serveur et le premier rendu
    // client affichent la même chose (aucun contenu tiers), ce qui évite les erreurs d'hydratation.
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => this.load());
    }
  }

  isAllowed(service: ConsentTarget): boolean {
    return this._choices()[service];
  }

  acceptAll(): void {
    this.save({ maps: true, youtube: true });
  }

  refuseAll(): void {
    this.save({ ...NO_CONSENT });
  }

  save(choices: ConsentChoices): void {
    this._choices.set({ ...choices });
    this._decided.set(true);
    this._preferencesOpen.set(false);
    this.write(choices);
  }

  /** Accepte un seul service (bouton présent sur le contenu bloqué), sans toucher aux autres. */
  allow(service: ConsentTarget): void {
    this.save({ ...this._choices(), [service]: true });
  }

  openPreferences(): void {
    this._draft.set({ ...this._choices() });
    this._preferencesOpen.set(true);
  }

  setDraft(service: ConsentTarget, checked: boolean): void {
    this._draft.update(draft => ({ ...draft, [service]: checked }));
  }

  saveDraft(): void {
    this.save(this._draft());
  }

  closePreferences(): void {
    this._preferencesOpen.set(false);
  }

  private load(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const stored = JSON.parse(raw) as StoredConsent;
        const fresh = Date.now() - stored.savedAt < VALIDITY_MS;
        if (stored.version === STORAGE_VERSION && fresh) {
          this._choices.set({
            maps: stored.choices?.maps === true,
            youtube: stored.choices?.youtube === true,
          });
          this._decided.set(true);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch {
      // Stockage indisponible ou contenu illisible : on considère qu'aucun choix n'a été fait.
    }
    this._ready.set(true);
  }

  private write(choices: ConsentChoices): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      const stored: StoredConsent = { version: STORAGE_VERSION, savedAt: Date.now(), choices };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch {
      // Stockage indisponible : le choix vaut pour la session en cours seulement.
    }
  }
}
